// FR-M9: one calendar provider's row in Settings' Calendars tab (and the Meetings page's connect
// prompt before anything is connected) — connection state, the connected account, and the
// Connect / Disconnect actions. A provider the server has no OAuth app for (`configured: false`)
// is shown only to staff, who can configure it, as "Unavailable" with one line; a user can't do
// anything about it, so for them it isn't there (`providerVisible`).
//
// SD50: the OAuth clients are configured by Grackle staff in the operator console, never env
// vars, so platform admins get a link straight there.

import { Link } from "react-router-dom";
import { Badge, Button, Callout } from "@grackle/ui";
import { calendarStartUrl, calendarWriteStartUrl, type CalendarReturnTo } from "../calendar/client";
import type { CalendarProvider } from "../calendar/connectNotice";
import type { CalendarProviderStatus } from "../calendar/types";
import "../calendar/calendar.css";

/** Whether a provider's row is worth showing: always unless the server can't connect it, and then
 * only to staff (who can configure it) or in stub mode (the sample calendar still works). */
export function providerVisible(info: CalendarProviderStatus | undefined, stub: boolean, platformAdmin: boolean): boolean {
  return info === undefined || info.configured || info.connected || stub || platformAdmin;
}

export interface CalendarProviderBlockProps {
  label: string;
  provider: CalendarProvider;
  orgId: string;
  /** `undefined` while status loads or if it failed to load. */
  info?: CalendarProviderStatus;
  stub: boolean;
  busy: string | null;
  onStub: () => void;
  onDisconnect: (id: string) => void;
  /** FR-AD7 staff: show a link to the operator console's Integrations tab when unconfigured. */
  platformAdmin?: boolean;
  /** Where Connect's OAuth round trip comes back to. */
  returnTo?: CalendarReturnTo;
}

export default function CalendarProviderBlock({
  label,
  provider,
  orgId,
  info,
  stub,
  busy,
  onStub,
  onDisconnect,
  platformAdmin = false,
  returnTo = "meetings",
}: CalendarProviderBlockProps) {
  const connected = info?.connected ?? false;
  // Unknown (status not loaded) is treated as connectable: the server redirects back with a
  // `provider_unconfigured` notice if it isn't, which beats hiding the only way forward.
  const unconfigured = info !== undefined && !info.configured;
  const disconnecting = info?.connection_id ? busy === `disc-${info.connection_id}` : false;
  if (!providerVisible(info, stub, platformAdmin)) return null;

  return (
    <div className="cal-provider" data-testid={`calendar-provider-${provider}`}>
      <div className="cal-provider-head">
        <span className="cal-provider-name">{label}</span>
        {connected ? (
          <Badge tone="success" icon="check-circle" size="sm">
            Connected{info?.stub ? " · sample" : ""}
          </Badge>
        ) : (
          <Badge tone="neutral" size="sm">
            {unconfigured ? "Unavailable" : "Not connected"}
          </Badge>
        )}
      </div>
      {connected && info ? (
        <>
          <div className="gk-sm">
            {info.stub ? "Sample calendar" : info.account_email ? `Account: ${info.account_email}` : "Connected"}
          </div>
          <div className="cal-provider-actions">
            {info.configured && !info.stub ? (
              <a className="gk-btn gk-btn--secondary gk-btn--sm" href={calendarWriteStartUrl(provider, orgId, returnTo)}>
                Allow Grackle to add notes to calendar events
              </a>
            ) : null}
            {info.connection_id ? (
              <Button
                variant="danger"
                size="sm"
                icon="link-break"
                onClick={() => onDisconnect(info.connection_id!)}
                disabled={disconnecting}
              >
                {disconnecting ? "Disconnecting…" : "Disconnect"}
              </Button>
            ) : null}
          </div>
        </>
      ) : (
        <>
          {unconfigured ? (
            <div className="gk-sm">
              Not set up on this server yet.
              {platformAdmin ? (
                <>
                  {" "}
                  <Link to="/ops/integrations">Configure integrations</Link>
                </>
              ) : null}
            </div>
          ) : (
            <div className="gk-sm">Read-only access to upcoming meetings. You&apos;ll confirm on {label}&apos;s consent screen.</div>
          )}
          {!unconfigured || stub ? (
            <div className="cal-provider-actions">
              {!unconfigured ? (
                <a className="gk-btn gk-btn--primary gk-btn--sm" href={calendarStartUrl(provider, orgId, returnTo)}>
                  Connect {label}
                </a>
              ) : null}
              {stub ? (
                <Button variant="secondary" size="sm" onClick={onStub} disabled={busy === `stub-${provider}`}>
                  {busy === `stub-${provider}` ? "Connecting…" : "Use sample calendar"}
                </Button>
              ) : null}
            </div>
          ) : null}
        </>
      )}
      {info?.last_error ? (
        <Callout tone="danger" icon="warning-circle">
          {info.last_error}
        </Callout>
      ) : null}
    </div>
  );
}
